/* MultipleDraftPdfPreview.jsx */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import { motion, AnimatePresence } from 'framer-motion';
import { InventoryDocumentTypes } from '../../../assests/Constants/constants';

/* util: base64 → Blob-URL */
const base64ToBlobUrl = (b64, mime = 'application/pdf') => {
  const bin = atob(b64);
  const uint = Uint8Array.from(bin, c => c.charCodeAt(0));
  return URL.createObjectURL(new Blob([uint], { type: mime }));
};

const MultipleDraftPdfPreview = ({
  open = false,
  results = [],          // [{fileName, pdfBase64}, …]
  onCancel = () => { },
  onOk = () => { },
  t = s => s,
  withCancel = true,
  documentType = null,   // DocumentType to determine which approval title to show
  confirmLoading = false, // e.g. final ledger invoice API in flight
  title = null,
  okLabel = null,
  cancelLabel = null
}) => {
  /* ---- hooks (always run in same order) --------------------------- */
  const [idx, setIdx] = useState(0);
  const [originalIdx, setOriginalIdx] = useState(0); // For navigating multiple original documents
  const [generatedIdx, setGeneratedIdx] = useState(0); // For navigating multiple generated documents
  const [url, setUrl] = useState('');
  const [url1, setUrl1] = useState(''); // For side-by-side mode (Original)
  const [url2, setUrl2] = useState(''); // For side-by-side mode (Generated)
  const [dir, setDir] = useState(1);         // slide direction

  // Reset indices when results change or modal opens/closes
  useEffect(() => {
    setIdx(0);
    setOriginalIdx(0);
    setGeneratedIdx(0);
  }, [results, open]);

  // Organize documents
  const originalDocs = results.filter(r => r.isOriginal || r.fileName?.includes('Uploaded'));
  const generatedDocs = results.filter(r => r.isGenerated || r.fileName?.includes('Generated'));
  
  // Side-by-side only when we actually have a distinct pair: originals + generated.
  // This keeps invoice/receipt “many drafts” as a single-pager instead of splitting left/right.
  const isSideBySide = originalDocs.length > 0 && generatedDocs.length > 0;

  const sumAmount = (list) =>
    (Array.isArray(list) ? list : []).reduce((s, r) => s + (Number(r?.meta?.amount) || 0), 0);
  const totalAmount = sumAmount(results);
  const hasAmounts = (Array.isArray(results) ? results : []).some(r => r?.meta?.amount !== undefined && r?.meta?.amount !== null);
  const resolvedTitle = title || t('DraftPreview');
  const resolvedOkLabel = okLabel || t('OK');
  const resolvedCancelLabel = cancelLabel || t('Cancel');

  const summaryRows = (Array.isArray(results) ? results : []).map((r, i) => ({
    i,
    fileName: r?.fileName || r?.FileName || '',
    clientName: r?.meta?.clientName || '',
    caseId: r?.meta?.caseId || '',
    reference: r?.meta?.reference || r?.meta?.Reference || '',
    amount: r?.meta?.amount,
    paymentIdsCount: Array.isArray(r?.meta?.paymentIds) ? r.meta.paymentIds.length : null,
  }));

  /* keep blob-url in sync & cleaned-up */
  useEffect(() => {
    if (!results.length) { 
      setUrl(''); 
      setUrl1(''); 
      setUrl2(''); 
      return; 
    }

    const processUrl = (pdfBase64, setter) => {
      if (!pdfBase64) {
        setter('');
        return;
      }

      // Check if it's a URL (http, https, blob, or s3://)
      const isUrl = typeof pdfBase64 === 'string' && (
        pdfBase64.startsWith('http://') || 
        pdfBase64.startsWith('https://') || 
        pdfBase64.startsWith('blob:') ||
        pdfBase64.startsWith('s3://')
      );

      if (isUrl) {
        // For S3 URLs, we can't display them directly - they need to be presigned
        // But if we're here, it means the backend should have provided presigned URLs
        if (pdfBase64.startsWith('s3://')) {
          console.warn('Received S3 URL instead of presigned URL:', pdfBase64);
          setter('');
          return;
        }
        setter(pdfBase64);
      } else {
        // Try to decode as base64
        try {
          const blob = base64ToBlobUrl(pdfBase64);
          setter(blob);
          return () => URL.revokeObjectURL(blob);
        } catch (error) {
          console.error('Failed to decode base64 PDF:', error, 'Value:', pdfBase64?.substring(0, 100));
          setter('');
        }
      }
    };

    if (isSideBySide) {
      // Improved logic for side-by-side:
      // If we have distinct categories, use them. 
      // Otherwise, use first N-1 as originals and last one as generated.
      let leftDocs = originalDocs;
      let rightDocs = generatedDocs;

      if (leftDocs.length === 0 || rightDocs.length === 0) {
        // Fallback: use all but last as left, and last as right
        leftDocs = results.slice(0, results.length - 1);
        rightDocs = [results[results.length - 1]];
      }

      processUrl(leftDocs[originalIdx]?.pdfBase64, setUrl1);
      processUrl(rightDocs[generatedIdx]?.pdfBase64, setUrl2);
    } else {
      // Single document mode: process current document
      const cur = results[idx];
      processUrl(cur?.pdfBase64, setUrl);
    }
  }, [results, idx, originalIdx, generatedIdx, isSideBySide]);

  /* ---------------------------------------------------------------- */
  if (!open) return null;                    // <- early return *after* hooks

  const canPrev = idx > 0;
  const canNext = idx < results.length - 1;

  // Final category resolution for JSX
  let leftDocs = originalDocs;
  let rightDocs = generatedDocs;
  if (isSideBySide && (leftDocs.length === 0 || rightDocs.length === 0)) {
    leftDocs = results.slice(0, results.length - 1);
    rightDocs = [results[results.length - 1]];
  }

  // Helper function to get the approval title based on documentType
  const getApprovalTitle = () => {
    if (documentType === InventoryDocumentTypes.StockIntake) {
      return t('IntakeApproval');
    } else if (documentType === InventoryDocumentTypes.StockRelease) {
      return t('ExitApproval');
    } else if (documentType === InventoryDocumentTypes.DeliveryNote) {
      return t('DeliveryNoteApproval');
    } else if (documentType === InventoryDocumentTypes.ReturnNote) {
      return t('ReturnNoteApproval');
    }
    // Default fallback
    return t('IntakeApproval');
  };

  const getDocumentTitle = () => {
    if (documentType === InventoryDocumentTypes.StockIntake) {
      return t('IntakeDocument');
    } else if (documentType === InventoryDocumentTypes.StockRelease) {
      return t('ExitDocument');
    } else if (documentType === InventoryDocumentTypes.DeliveryNote) {
      return t('DeliveryNote');
    } else if (documentType === InventoryDocumentTypes.ReturnNote) {
      return t('ReturnNote');
    }
    return t('IntakeDocument');
  };


  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,.67)',
        zIndex: 3000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Paper
        elevation={10}
        sx={{
          width: 'min(1600px, 96vw)',
          height: 'min(920px, 92vh)',
          borderRadius: 2,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#304FFF', lineHeight: 1.1 }}>
              {resolvedTitle}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Chip size="small" label={`${t('Selected')}: ${results.length}`} />
              {hasAmounts && (
                <Chip size="small" color="primary" variant="outlined" label={`${t('Amount') || 'Amount'}: ${totalAmount.toFixed(2)}`} />
              )}
              {!isSideBySide && results.length > 1 && (
                <Chip size="small" variant="outlined" label={`${idx + 1} / ${results.length}`} />
              )}
            </Box>
          </Box>
        </Box>

        <Divider />

        {/* Summary table */}
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography sx={{ fontWeight: 700, mb: 1, color: '#304FFF' }}>
            {t('Summary') || 'Summary'}
          </Typography>
          <TableContainer sx={{ maxHeight: 180, border: '1px solid #eee', borderRadius: 1 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>{t('No') || '#'}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t('Client') || 'Client'}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t('Reference') || 'Reference'}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t('CaseId') || 'CaseId'}</TableCell>
                  <TableCell sx={{ fontWeight: 700, textAlign: 'right' }}>{t('Amount') || 'Amount'}</TableCell>
                  <TableCell sx={{ fontWeight: 700, textAlign: 'right' }}>{t('Payments') || 'Payments'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summaryRows.map((r) => (
                  <TableRow key={`${r.caseId}-${r.i}`} selected={!isSideBySide && r.i === idx}>
                    <TableCell>{r.i + 1}</TableCell>
                    <TableCell sx={{ maxWidth: 260, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {r.clientName || '—'}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 180, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {r.reference || '—'}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 220, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {r.caseId || '—'}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'right' }}>
                      {typeof r.amount === 'number' ? r.amount.toFixed(2) : '—'}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'right' }}>
                      {typeof r.paymentIdsCount === 'number' ? r.paymentIdsCount : '—'}
                    </TableCell>
                  </TableRow>
                ))}
                {!summaryRows.length && (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ color: '#777' }}>
                      {t('NothingReturned') || 'Nothing returned'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Divider />

        {/* Preview area */}
        <Box sx={{ flex: 1, px: 2, py: 1.5, overflow: 'hidden' }}>

      {isSideBySide ? (
        /* --------- Side-by-side mode (at least 2 documents total) --------- */
        <Box sx={{ display: 'flex', gap: 2, height: '100%' }}>
          {/* Left side: Original Documents / Preceding Documents */}
          <Box sx={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography sx={{ fontWeight: 700, color: '#304FFF', textAlign: 'center' }}>
              {getDocumentTitle()} {leftDocs.length > 1 ? `(${originalIdx + 1}/${leftDocs.length})` : ''}
            </Typography>
            <Box sx={{ flex: 1, border: '1px solid #E0E0E0', borderRadius: 1.5, overflow: 'hidden', position: 'relative' }}>
              {url1 ? (
                <iframe src={url1} title={leftDocs[originalIdx]?.fileName || 'Document'} style={{ width: '100%', height: '100%', border: 'none' }} />
              ) : (
                <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#777' }}>
                  {t('NothingReturned') || 'Nothing returned'}
                </Box>
              )}

              {/* Navigation arrows for left side documents */}
              {leftDocs.length > 1 && (
                <>
                  <Box
                    sx={{
                      position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
                      width: 40, height: 40, borderRadius: '50%', background: 'rgba(255,255,255,0.9)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                      boxShadow: '0 2px 5px rgba(0,0,0,0.2)', visibility: originalIdx > 0 ? 'visible' : 'hidden'
                    }}
                    onClick={() => setOriginalIdx(prev => Math.max(0, prev - 1))}
                  >
                    <ArrowBackIcon sx={{ color: '#304FFF' }} />
                  </Box>
                  <Box
                    sx={{
                      position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                      width: 40, height: 40, borderRadius: '50%', background: 'rgba(255,255,255,0.9)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                      boxShadow: '0 2px 5px rgba(0,0,0,0.2)', visibility: originalIdx < leftDocs.length - 1 ? 'visible' : 'hidden'
                    }}
                    onClick={() => setOriginalIdx(prev => Math.min(leftDocs.length - 1, prev + 1))}
                  >
                    <ArrowForwardIcon sx={{ color: '#304FFF' }} />
                  </Box>
                </>
              )}
            </Box>
          </Box>

          {/* Right side: Generated Document / Final Document */}
          <Box sx={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography sx={{ fontWeight: 700, color: '#304FFF', textAlign: 'center' }}>
              {getApprovalTitle()} {rightDocs.length > 1 ? `(${generatedIdx + 1}/${rightDocs.length})` : ''}
            </Typography>
            <Box sx={{ flex: 1, border: '1px solid #E0E0E0', borderRadius: 1.5, overflow: 'hidden', position: 'relative' }}>
              {url2 ? (
                <iframe src={url2} title={rightDocs[generatedIdx]?.fileName || 'Generated Document'} style={{ width: '100%', height: '100%', border: 'none' }} />
              ) : (
                <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#777' }}>
                  {t('NothingReturned') || 'Nothing returned'}
                </Box>
              )}

              {/* Navigation arrows for right side generated documents */}
              {rightDocs.length > 1 && (
                <>
                  <Box
                    sx={{
                      position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
                      width: 40, height: 40, borderRadius: '50%', background: 'rgba(255,255,255,0.9)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                      boxShadow: '0 2px 5px rgba(0,0,0,0.2)', visibility: generatedIdx > 0 ? 'visible' : 'hidden'
                    }}
                    onClick={() => setGeneratedIdx(prev => Math.max(0, prev - 1))}
                  >
                    <ArrowBackIcon sx={{ color: '#304FFF' }} />
                  </Box>
                  <Box
                    sx={{
                      position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                      width: 40, height: 40, borderRadius: '50%', background: 'rgba(255,255,255,0.9)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                      boxShadow: '0 2px 5px rgba(0,0,0,0.2)', visibility: generatedIdx < rightDocs.length - 1 ? 'visible' : 'hidden'
                    }}
                    onClick={() => setGeneratedIdx(prev => Math.min(rightDocs.length - 1, prev + 1))}
                  >
                    <ArrowForwardIcon sx={{ color: '#304FFF' }} />
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </Box>
      ) : (
        <Box sx={{ height: '100%', border: '1px solid #E0E0E0', borderRadius: 1.5, overflow: 'hidden', position: 'relative' }}>
          <AnimatePresence mode="wait">
            {url && (
              <motion.iframe
                key={url}
                src={url}
                title={results[idx]?.fileName || 'Draft PDF'}
                initial={{ opacity: 0, x: dir * 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: dir * -100 }}
                transition={{ duration: .1 }}
                style={{ width: '100%', height: '100%', border: 'none' }}
              />
            )}
          </AnimatePresence>
          {canNext && (
            <Box
              sx={{
                position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
                width: 44, height: 44, borderRadius: '50%',
                background: 'rgba(255,255,255,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
              }}
              onClick={() => { setDir(1); setIdx(i => Math.min(results.length - 1, i + 1)); }}
            >
              <ArrowForwardIcon sx={{ color: '#304FFF' }} />
            </Box>
          )}
          {canPrev && (
            <Box
              sx={{
                position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                width: 44, height: 44, borderRadius: '50%',
                background: 'rgba(255,255,255,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
              }}
              onClick={() => { setDir(-1); setIdx(i => Math.max(0, i - 1)); }}
            >
              <ArrowBackIcon sx={{ color: '#304FFF' }} />
            </Box>
          )}
          
        </Box>
      )}

        </Box>

        <Divider />

        {/* Footer buttons inside modal */}
        <Box sx={{ px: 2, py: 1.5, display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
          {withCancel && (
            <Button
              variant="outlined"
              color="error"
              onClick={onCancel}
              disabled={confirmLoading}
              sx={{ minWidth: 140 }}
            >
              <CloseIcon sx={{ mr: 0.5 }} /> {resolvedCancelLabel}
            </Button>
          )}
          <Button
            variant="contained"
            onClick={onOk}
            disabled={confirmLoading}
            sx={{ bgcolor: '#304FFF', minWidth: 180 }}
          >
            {confirmLoading ? (
              <CircularProgress size={22} sx={{ color: '#fff' }} />
            ) : (
              <>
                <CheckIcon sx={{ mr: 0.5 }} /> {resolvedOkLabel}
              </>
            )}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default MultipleDraftPdfPreview;
